import React, { useState, useEffect } from 'react';
import { useBackendFilter } from '../../hooks/usePosts';
import { ITechCheckItem } from '../../types/type';
import Table, { ColumnConfig, ExpandedRowConfig, SecondaryRowConfig } from '../Table/Table';
import StatusCell from '../Table/StatusCell';
import { formatBackendShortDate, formatBackendTime } from '../../utils/date.utils';
import Pagination from '../Pagination/Pagination';

interface TechHistoryProps {
  selectedTechList: ITechCheckItem;
  // Можно передать те же конфигурации для таблицы, что используются в TechInfoCard
  tableColumns: ColumnConfig[];
  expandedRows: ExpandedRowConfig[];
  secondaryRows: SecondaryRowConfig[];
  statusMap: Record<string, { label: string; dotClass: string }>;
}

const TechHistory: React.FC<TechHistoryProps> = ({
  selectedTechList,
  tableColumns,
  expandedRows,
  secondaryRows,
  statusMap,
}) => {
  // Состояние для хранения истории технических проверок
  const [techCheckHistory, setTechCheckHistory] = useState<ITechCheckItem[]>([]);

  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);

  // Используем хук useBackendFilter для получения истории ТО
  const {
    data: techListsHistory,
    loading: historyLoading,
    error: historyError,
    totalCount,
    totalPages,
    fetchData: fetchTechHistory,
  } = useBackendFilter(`/techlist/technik/${selectedTechList.technicID}`);

  // Функция для получения данных истории технических проверок
  const fetchTechCheckHistory = async (page = 1) => {
    if (!selectedTechList?.profile?.id || !selectedTechList.profile.firstName || !selectedTechList.profile.lastName)
      return;

    try {
      const filters = {
        page,
        limit: 6, // Запрашиваем 6 записей (чтобы потом отфильтровать текущую и осталось 5)
        firstName: selectedTechList.profile.firstName,
        lastName: selectedTechList.profile.lastName,
      };

      // Запрашиваем данные через хук
      await fetchTechHistory(filters);
    } catch (err) {
      console.error('Ошибка при получении истории ТО:', err);
    }
  };

  // Обработка данных после получения от API
  useEffect(() => {
    if (techListsHistory && techListsHistory.length > 0 && selectedTechList) {
      // Отфильтровываем текущее ТО из списка истории (если оно там есть)
      const filteredHistory = techListsHistory.filter((item: Record<string, any>) => item.id !== selectedTechList.id);

      // Берем только первые 5 записей
      const limitedItems = filteredHistory.slice(0, 5);

      // Используем данные напрямую
      setTechCheckHistory(limitedItems as ITechCheckItem[]);
    }
  }, [techListsHistory, selectedTechList]);

  // Функция для изменения страницы
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchTechCheckHistory(newPage);
    }
  };

  // Загружаем данные при изменении профиля водителя
  useEffect(() => {
    if (selectedTechList?.profile?.id && selectedTechList.profile.firstName && selectedTechList.profile.lastName) {
      setCurrentPage(1);
      fetchTechCheckHistory(1);
    }
  }, [selectedTechList?.profile?.id, selectedTechList?.profile?.firstName, selectedTechList?.profile?.lastName]);

  return (
    <div className="infoCard">
      <div className="infoCardHeader">
        <div className="infoCardHeaderTitle">История технических проверок</div>
      </div>
      <div className="techinfoHistoryContainer">
        {historyLoading ? (
          <div className="loading-indicator">Загрузка...</div>
        ) : historyError ? (
          <div className="error-message">{historyError}</div>
        ) : techCheckHistory.length > 0 ? (
          <>
            <Table
              data={techCheckHistory}
              columns={tableColumns}
              expandedRows={expandedRows}
              secondaryRows={secondaryRows}
              keyField="id"
              noDataMessage="Нет данных для отображения"
            />

            {totalPages > 1 && (
              <div className="pagination-container">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            )}
          </>
        ) : (
          <div className="no-data-message">Нет истории технических проверок</div>
        )}
      </div>
    </div>
  );
};

export default TechHistory;
